import { ResultCardObject } from '../search-result.model';
import {
  IAdvancedSearch,
  IFactStatus,
  IFactStatusValue,
} from '../services/apiServices';
import { getResultCardContent } from './result-card.util';

// The API can return a non-object body (e.g. a string when the ES index does
// not exist yet), so every consumer reads `result` through this helper.
export const getResultArray = (
  apiResponse?: Partial<IAdvancedSearch> | null
): IAdvancedSearch['result'] =>
  Array.isArray(apiResponse?.result) ? apiResponse.result : [];

export const getResultCardDataFromApiResponse = (
  apiResponse: IAdvancedSearch
): ResultCardObject[] => {
  return getResultArray(apiResponse).flatMap(
    (module) =>
      module.searchResult?.flatMap((result) =>
        getResultCardContent(
          result.fields,
          module.module,
          result.objectdefination,
          result.name,
          result.label,
          result.detailViewName
        )
      ) ?? []
  );
};

export const getQuickFiltersFromApiResponse = (
  apiResponse: IAdvancedSearch
): string[] => {
  const results = getResultArray(apiResponse);

  if (results.length === 0) return [];

  if (results.length > 1) {
    const modules = results.map(
      (result) => `${result.module} (${result?.resultCount})`
    );

    const resultCountAllApplications = results.reduce(
      (sum, result) => sum + result.resultCount,
      0
    );

    return [`All Application (${resultCountAllApplications})`, ...modules];
  }

  return [`${results[0].module} (${results[0].resultCount})`];
};

export const getFacetApiPayloadFromApiResponse = (
  apiResponse: IAdvancedSearch
) => {
  return getResultArray(apiResponse).flatMap(
    (module) =>
      module.searchResult?.flatMap((result) => {
        return result.index;
      }) ?? []
  );
};

export const getFaucetListFromApiResponse = (
  apiResponse: IAdvancedSearch,
  currentModule: string | null
) => {
  const currentModuleNoCount = currentModule?.split('(')?.[0].trim();

  if (
    !currentModuleNoCount ||
    currentModuleNoCount === 'undefined' ||
    getResultArray(apiResponse).length === 0
  )
    return [];

  if (!apiResponse?.Fact) return [];

  // IFact's declared type says `{[key]: IFactStatus}`, but at runtime the
  // backend returns `{[module]: {[factKey]: IFactStatus}}` — cast through
  // unknown to match reality.
  const factByModule = apiResponse.Fact as unknown as Record<
    string,
    Record<string, IFactStatus>
  >;

  const facts =
    currentModuleNoCount === 'All Application'
      ? aggregateFactsAcrossModules(factByModule)
      : Object.values(factByModule[currentModuleNoCount] ?? {});

  return facts.map((fact) => ({
    key: fact.key,
    label: fact.name,
    items: fact.values,
    dataType: fact.dataType,
    module: currentModuleNoCount === 'All Application' ? 'All' : fact.module,
  }));
};

// "All Application" is a synthetic tab that spans every module, but the API
// only emits facets keyed by real module name. Merge facets sharing a key
// across modules and sum doc_count on overlapping values so the refine panel
// shows one entry per field (e.g. a single Status accordion) instead of per
// module.
const aggregateFactsAcrossModules = (
  factByModule: Record<string, Record<string, IFactStatus>>
): IFactStatus[] => {
  const merged = new Map<string, IFactStatus>();

  for (const moduleFacts of Object.values(factByModule)) {
    if (!moduleFacts) continue;
    for (const fact of Object.values(moduleFacts)) {
      if (!fact) continue;
      const existing = merged.get(fact.key);
      if (!existing) {
        merged.set(fact.key, {
          ...fact,
          values: (fact.values ?? []).map((v) => ({ ...v })),
        });
        continue;
      }
      const valueByKey = new Map<string, IFactStatusValue>(
        existing.values.map((v) => [v.key, v])
      );
      for (const value of fact.values ?? []) {
        const prev = valueByKey.get(value.key);
        valueByKey.set(
          value.key,
          prev
            ? { key: value.key, doc_count: prev.doc_count + value.doc_count }
            : { ...value }
        );
      }
      existing.values = Array.from(valueByKey.values());
    }
  }

  return Array.from(merged.values());
};
